/* eslint-disable no-use-before-define */
import { AnimationType } from '@ve-pro/enums/animation'
import {
  AiTaskStatus,
  AnimationResource,
  AssetSource,
  AssetStatus,
  AssetType,
  AssetVisible,
  CommonAssetType,
  FileExt,
  shotStatus,
  AssetAlpha,
} from '@ve-pro/enums/asset'
import type { TableDataConfig } from '@pt/api/assets/index.type'
import { Bool } from '@ve-pro/enums/segment'
import type {
  ProjectModel,
  ProjectSegmentModel,
  TemplatePreviews,
} from '../project/type'
import type { VideoTemplateItemType } from '@pt/api/template/index.type'

export interface GetPreUploadParams {
  md5: string | number
  filename: string
  filesize: number
  type: string
  pid: number
}

export interface GetAssetProgressParams {
  ids: string
}

export interface GetProjectAssetsParams {
  id: number
  type?: string
}

export interface DeleteProjectAssetsParams {
  pid: number
  aid: number
}

export interface GetAssetListsParams {
  type?: number
  group_id?: string
  tag_ids?: string
  name?: string
  adapter_resource?: AnimationResource
  cartoon_types?: AnimationType[]
  page: number
  page_size: number
}

export interface SearchAssetParams {
  // 文件格式
  formats?: string[]
  // 方向--1横，2竖，3方
  directions?: number[]
  // 比例
  ratios?: string[]
  // 文件--最小值
  size_min?: number
  // 文件--最大值
  size_max?: number
  // 宽度--最小值
  width_min?: number
  // 宽度--最大值
  width_max?: number
  // 高度--最小值
  height_min?: number
  // 高度--最大值
  height_max?: number
  // 码率--最小值
  bitrate_max?: number
  // 码率--最大值
  bitrate_min?: number
  // 时长最小值
  duration_min?: number
  // 时长最大值
  duration_max?: number
  // 文件单位--KB、MB
  size_unit?: 'KB' | 'MB'
  // 时长单位--秒、分、小时
  duration_unit?: 'sec' | 'min' | 'hour'
}

export type AnimationAllType = Required<GetAssetListsParams>['cartoon_types'][0]

export interface PreUploadModel {
  id: number
  part_size: number
  part_total: number
  second_trans: boolean
  status: string
  upload_id: number | string
}

export interface ProjectAssetsModel {
  // 仅作为资源组的资源的时候，该字段表示是否默认选中
  is_default: Bool

  // 广告主id
  adv_id: number

  // 创建时间
  created_time: number

  // 文件格式
  file_ext: FileExt

  // 文件大小
  file_size: number

  // 文件名称
  file_name: string

  // 源始文件路径
  file_url: string

  file_md5: string

  // 转码后无声视频
  out_mp4_url: string

  // 转码后无声360p的预览视频
  out_mp4_preview_url: string

  // 视频抽离出的音频
  out_mp3_url: string

  /** 分离出来的人声音频 */
  vocals_url?: string
  /** 分离出来的bgm音频 */
  bgm_url?: string

  // 文件高度
  height: number

  // 文件id
  id: number

  // 文件进度id
  asset_id: number

  // 文件格式
  mime_type: string

  // 文件类型
  type: AssetType

  // 文件时长
  duration: number

  // 文件宽度
  width: number

  // 文件状态---是否存在转码中的文件
  status: AssetStatus

  // 文件封面
  thumb_url: string

  // 项目名称
  name: string

  // 项目比例，比如 9:16
  ratio: string

  // 更新时间
  updated_time: number

  // 进度
  progress: number

  // 资源来源
  source: AssetSource
  // 如果是公共素材库的，那么有此id
  source_id: number | string

  source_active?: AssetSource

  // 序列帧、滤镜、转场
  file_urls: string[] | null

  postscript_name?: string

  text: string
  // 帧
  frame_rate: number

  // 分镜状态
  shot_status: shotStatus | null

  // 关键帧
  shot_frame_ids: string

  // 分镜task_id
  shot_task_id?: number | null

  // 资源是否可用（不可用的情况有被下架和删除），true为可用，false为不可用
  enable?: boolean

  alpha: AssetAlpha
}

export interface AsssetsModel {
  // 标签素材商用
  copyright: number
  // 标签文件路径
  file_url: string
  // 标签id
  id: number
  // 标签名称
  name: string
  // 标签封面
  thumb_url: string
  // 标签类型
  type: CommonAssetType
  // 标签时长
  duration: number
  // 标签格式
  format: FileExt
  // 标签预览路径
  file_urls: string[]
  // 视频、图片的宽
  width: number
  // 视频、图片的高
  height: number
  // 标签是否收藏
  is_favorite: boolean
  // 音频播放id
  playId?: string
  // 文件md5
  file_md5: string
  // 2内部/1外部 资源
  public: AssetVisible
}

export interface ListAssetsModel {
  // 主标签id
  id: string
  // 主标签名称
  name: string
  // 主标签类型
  type: number
  // 主标签数组
  assets: AsssetsModel[]
}

export type SubTagsModel = {
  id: AsssetsModel['id']
  name: AsssetsModel['name']
  type: AsssetsModel['type']
}

export interface ListAssistModel {
  // 辅助标签id
  id: string
  // 辅助标签名称
  name: string
  // 辅助标签类型
  type?: number
  // 替换辅助标签数组字段
  option: SubTagsModel[]
  // 辅助标签数组
  sub_tags?: SubTagsModel[]
}

export interface ProjectAssets {
  assets: ProjectAssetsModel[] | null
}

export interface ListAssets {
  list: ListAssetsModel[] | null
}

export interface ListAssists {
  list: ListAssistModel[] | null
}
export interface AssetList {
  list: AsssetsModel[] | null
  total: number
}

export interface AssetAdd {
  assets: ProjectAssetsModel[] | null
}

export interface AssetCreate {
  assets: (ProjectAssetsModel & { ext_id: string })[] | null
}

export interface AssetAddParams {
  ids: (FontModel['id'] | AsssetsModel['id'] | TableDataConfig['id'])[]
  project_id: ProjectModel['id']
  is_original?: boolean // 上传文件同步到项目素材需要增加此参数
}

export interface TransProgress
  extends Pick<
    ProjectAssetsModel,
    'file_url' | 'out_mp4_url' | 'out_mp4_preview_url' | 'out_mp3_url'
  > {
  id: ProjectAssetsModel['asset_id']
  status: ProjectAssetsModel['status']
  progress: number
}

export type FontModel = {
  // 文件id
  id: number

  label: string

  // 文件封面
  svg: string

  value: string

  // 源始文件路径
  url: string
}

export interface PresetFontModel {
  id: number
  thumb_url: string
  file_urls: [string]
}

export interface AnimationModel {
  id: number
  file_urls: [string]
  file_url: string
  name: string
}

export const Tool = 2 // 应用工具 1 可玩工具 2 视频编辑器 3 录屏工具

export interface AssetCreateModel {
  asset_type: AssetType
  file_md5?: string
  file_url: string
  name?: string
  ext_id?: string // 分镜不需要传
}

export interface AssetCreateParams {
  project_id: ProjectModel['id']
  is_sync: boolean // 是否同步到项目资源
  assets: AssetCreateModel[]
}
export interface AsrModel {
  audio_url: string
  duration: number
  id: number
  rate: number
  src_language: string
  start_time: number
}

export interface AsrRes {
  ids: QueryAsrResModel['id'][]
}

export interface QueryAsrResModel {
  id: number
  param_id: number
  result: QueryAsrModel
  status: AiTaskStatus
}

export interface SentencesModel {
  begin_time: number
  end_time: number
  text: string
  id: number
}

export type AsrSentencesModel = Pick<
  SentencesModel,
  'begin_time' | 'end_time' | 'text'
>

export interface QueryAsrModel {
  duration: number
  error?: string
  id: AsrModel['id']
  sentences: AsrSentencesModel[]
  track_id: string
}

export interface ReplaceAssetParams {
  // 资源id
  asset_id?: ProjectAssetsModel['asset_id']
  // 被替换的资源id
  project_asset_id?: ProjectAssetsModel['id']
  project_id: ProjectModel['id']
  // 不传就是替换所有相关元件的资源
  segment_id?: ProjectSegmentModel['id']
  // 公共库
  common_id?: number
}

export interface FragmentParams {
  duration_max?: number
  duration_min?: number
  duration_range?: string
  duration_unit?: string
  keyword?: string
  order?: string
  ratios?: string[]
  src_project_id?: number
  tag_ids?: string
  template_id?: number
  fragment_segment_ids?: string
}

export interface VeditorTemplateModel {
  child_ids: string
  id: number
  name: string
  templates: VideoTemplateItemType[]
}

export interface FragmentListModel {
  cover_url: string
  duration: number
  fragment_segment_id: number
  id: number
  name: string
  name_en: string
  name_jp: string
  cover_previews: TemplatePreviews
  previews: TemplatePreviews
  ratio: string
  ratios: string
  tag_ids: string
  language: string
}

interface FragmentTagItem {
  id: string
  name: string
  name_en: string
  sub_tag: { id: string; name: string; name_en: string }[] | null
}

export interface FragmentTagsModel {
  id: string
  name: string
  name_en: string
  sub_tag: FragmentTagItem[] | null
}
